/**
 * Glavna komponenta: FAB, modal sa podešavanjima, opcioni TOC panel.
 *
 * - Renderuje se preko createPortal(portalTarget) — podrazumevano document.body.
 * - Učitava styles/accessibility-panel.css (panel + enactori + host overrides u istom fajlu).
 * - config: vidi INTEGRATION.md i core/defaultConfig.js
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CONTRAST_OPTIONS, FONT_OPTIONS } from '../core/constants';
import { mergeConfig } from '../core/prefs';
import PreferenceSection from './PreferenceSection';
import Stepper from './Stepper';
import TableOfContents from './TableOfContents';
import ToggleSwitch from './ToggleSwitch';
import { useAccessibilityPreferences } from './useAccessibilityPreferences';
import '../styles/accessibility-panel.css';

export default function AccessibilityPanel({ config: userConfig }) {
  const config = useMemo(() => mergeConfig(userConfig), [userConfig]);
  const { prefs, setPrefs, reset } = useAccessibilityPreferences(config);
  const [open, setOpen] = useState(false);
  const modalRef = useRef(null);

  const { classPrefix, labels, features, showTrigger, portalTarget, mainContentSelector } = config;

  const toggle = useCallback(() => setOpen((v) => !v), []);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, close]);

  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current.focus();
    }
  }, [open]);

  const portalRoot = portalTarget ?? (typeof document !== 'undefined' ? document.body : null);
  if (!portalRoot) return null;

  const ui = (
    <>
      {showTrigger && (
        <button
          type="button"
          className={`${classPrefix}-uio-fab`}
          onClick={toggle}
          aria-label={open ? labels.closePanel : labels.openPanel}
          aria-expanded={open}
          title={labels.panelTitle}
        >
          {open ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
              <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
              <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
              <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
            </svg>
          )}
        </button>
      )}

      {open && (
        <div className={`${classPrefix}-uio-overlay`} onClick={close} role="presentation">
          <div
            ref={modalRef}
            className={`${classPrefix}-uio-modal`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={labels.panelTitle}
            tabIndex={-1}
          >
            <div className={`${classPrefix}-uio-modal-header`}>
              <h2 className={`${classPrefix}-uio-modal-title`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 8, verticalAlign: 'text-bottom' }}>
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                {labels.panelTitle}
              </h2>
              <button type="button" className={`${classPrefix}-uio-modal-close`} onClick={close} aria-label={labels.close}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className={`${classPrefix}-uio-body`}>
              {features.textSize && (
                <PreferenceSection
                  classPrefix={classPrefix}
                  title={labels.textSizeTitle}
                  description={labels.textSizeDesc}
                >
                  <Stepper
                    classPrefix={classPrefix}
                    labels={labels}
                    value={prefs.textSize}
                    min={0.5}
                    max={2}
                    step={0.1}
                    onChange={(v) => setPrefs((p) => ({ ...p, textSize: v }))}
                    formatValue={(v) => v.toFixed(1)}
                  />
                </PreferenceSection>
              )}

              {features.lineSpacing && (
                <PreferenceSection
                  classPrefix={classPrefix}
                  title={labels.lineSpacingTitle}
                  description={labels.lineSpacingDesc}
                >
                  <Stepper
                    classPrefix={classPrefix}
                    labels={labels}
                    value={prefs.lineSpacing}
                    min={0.5}
                    max={3}
                    step={0.1}
                    onChange={(v) => setPrefs((p) => ({ ...p, lineSpacing: v }))}
                    formatValue={(v) => v.toFixed(1)}
                  />
                </PreferenceSection>
              )}

              {features.font && (
                <PreferenceSection
                  classPrefix={classPrefix}
                  title={labels.fontTitle}
                  description={labels.fontDesc}
                  wide
                >
                  <div className={`${classPrefix}-uio-font-picker`}>
                    {FONT_OPTIONS.map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        className={`${classPrefix}-uio-font-btn${prefs.textStyle === f.key ? ` ${classPrefix}-uio-font-btn--active` : ''}`}
                        style={f.family ? { fontFamily: f.family } : undefined}
                        onClick={() => setPrefs((p) => ({ ...p, textStyle: f.key }))}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </PreferenceSection>
              )}

              {features.contrast && (
                <PreferenceSection
                  classPrefix={classPrefix}
                  title={labels.contrastTitle}
                  description={labels.contrastDesc}
                  wide
                >
                  <div className={`${classPrefix}-uio-contrast-grid`}>
                    {CONTRAST_OPTIONS.map((c) => (
                      <button
                        key={c.key}
                        type="button"
                        className={`${classPrefix}-uio-contrast-swatch${prefs.contrast === c.key ? ` ${classPrefix}-uio-contrast-swatch--active` : ''}`}
                        style={{
                          '--ap-swatch-bg': c.bg ?? '#ffffff',
                          '--ap-swatch-fg': c.fg ?? '#1a1a1a',
                          '--ap-swatch-border': c.fg ?? '#253965',
                        }}
                        onClick={() => setPrefs((p) => ({ ...p, contrast: c.key }))}
                        aria-label={c.label}
                        title={c.label}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </PreferenceSection>
              )}

              {features.toc && (
                <PreferenceSection
                  classPrefix={classPrefix}
                  title={labels.tocTitle}
                  description={labels.tocDesc}
                >
                  <ToggleSwitch
                    classPrefix={classPrefix}
                    labels={labels}
                    value={prefs.toc}
                    onChange={(v) => setPrefs((p) => ({ ...p, toc: v }))}
                  />
                </PreferenceSection>
              )}

              {features.enhanceInputs && (
                <PreferenceSection
                  classPrefix={classPrefix}
                  title={labels.enhanceTitle}
                  description={labels.enhanceDesc}
                >
                  <ToggleSwitch
                    classPrefix={classPrefix}
                    labels={labels}
                    value={prefs.enhanceInputs}
                    onChange={(v) => setPrefs((p) => ({ ...p, enhanceInputs: v }))}
                  />
                </PreferenceSection>
              )}
            </div>

            <div className={`${classPrefix}-uio-modal-footer`}>
              <button
                type="button"
                className={`${classPrefix}-uio-reset-btn`}
                onClick={reset}
                title={labels.resetTitle}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
                {labels.reset}
              </button>
            </div>
          </div>
        </div>
      )}

      {features.toc && (
        <TableOfContents
          active={prefs.toc}
          mainContentSelector={mainContentSelector}
          classPrefix={classPrefix}
          labels={labels}
        />
      )}
    </>
  );

  return createPortal(ui, portalRoot);
}
